// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { Construction, MoreHorizontal, Power, PowerOff, ScrollText } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ImportPlusIcon } from "@/components/icons";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/lib/auth/auth-provider";
import { PAGE_PERMISSIONS } from "@/lib/auth/page-permissions";
import { useDebugMode } from "@/lib/debug-mode";
import { cn } from "@/lib/utils";

interface TransformationActionsMenuProps {
  isActive: boolean;
  onToggleActive: () => void;
  onImportData?: () => void;
  triggerClassName?: string;
}

export function TransformationActionsMenu({
  isActive,
  onToggleActive,
  onImportData,
  triggerClassName,
}: TransformationActionsMenuProps) {
  const { t } = useTranslation("back-office");
  const { hasPermission } = useAuth();
  const { debugMode } = useDebugMode();
  const canEditTransformation = hasPermission(PAGE_PERMISSIONS.ingressTransformationsEdit);
  const canImport = hasPermission(PAGE_PERMISSIONS.ingressImportsEdit);
  if (!canEditTransformation && !canImport) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className={cn("h-8 w-8", triggerClassName)}>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={-32} className="dropdown-overlap">
        {/* View Sessions is still WIP (mocked) — debug-mode only for now. */}
        {debugMode && (
          <DropdownMenuItem
            onSelect={() => toast.warning(t("common.workInProgress"))}
            className="text-amber-600 underline focus:text-amber-600 dark:text-amber-400 dark:focus:text-amber-400"
          >
            <ScrollText className="h-4 w-4" />
            <Construction className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
            {t("transformations.list.actions.viewSessions")}
          </DropdownMenuItem>
        )}
        {onImportData && (
          <>
            {debugMode && <DropdownMenuSeparator />}
            <DropdownMenuItem onSelect={onImportData} disabled={!canImport}>
              <ImportPlusIcon className="h-4 w-4" />
              {t("imports.importData")}
            </DropdownMenuItem>
          </>
        )}
        {canEditTransformation && (
          <>
            {(debugMode || onImportData) && <DropdownMenuSeparator />}
            <DropdownMenuItem
              onSelect={onToggleActive}
              className={isActive ? "text-destructive focus:text-destructive" : ""}
            >
              {isActive ? (
                <>
                  <PowerOff className="h-4 w-4 text-destructive" />
                  {t("transformations.list.actions.deactivate")}
                </>
              ) : (
                <>
                  <Power className="h-4 w-4" />
                  {t("transformations.list.actions.activate")}
                </>
              )}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
